import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../../../shared/i18n';
import { childrenApi, settingsApi } from '../../api/guardians';

// Zodiac animals for avatar selection
// Must match Child::ZODIAC_ANIMALS.keys in Rails (Vietnamese zodiac uses buffalo and cat)
const ZODIAC_ANIMALS = [
  'rat', 'buffalo', 'tiger', 'cat', 'dragon', 'snake',
  'horse', 'goat', 'monkey', 'rooster', 'dog', 'pig'
];

export const ChildForm = ({ isEdit = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { childId } = useParams();
  const queryClient = useQueryClient();

  const [yearOfBirth, setYearOfBirth] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [avatar, setAvatar] = useState('');
  const [errors, setErrors] = useState({});

  // Fetch existing child data if editing
  const { data: childData } = useQuery({
    queryKey: ['child', childId],
    queryFn: () => childrenApi.get(childId),
    enabled: isEdit && !!childId,
  });

  useEffect(() => {
    if (childData?.data?.child) {
      const child = childData.data.child;
      setYearOfBirth(child.year_of_birth?.toString() || '');
      setUsername(child.username || '');
      setAvatar(child.avatar || 'dragon');
      // Don't populate password for security
    }
  }, [childData]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => childrenApi.create(data),
    onSuccess: async (response) => {
      queryClient.invalidateQueries(['dashboard']);
      queryClient.invalidateQueries(['children']);

      // Check if wizard mode is enabled
      try {
        const settingsResponse = await settingsApi.get();
        const wizardMode = settingsResponse?.data?.settings?.kid_creation_wizard_mode;

        if (wizardMode && response?.data?.child?.id) {
          // Navigate to wizard
          navigate(`/children/${response.data.child.id}/wizard`);
        } else {
          // Navigate to dashboard as usual
          sessionStorage.setItem('flash_notice', 'Đã thêm trẻ thành công');
          navigate('/');
        }
      } catch {
        // If settings fetch fails, just go to dashboard
        sessionStorage.setItem('flash_notice', 'Đã thêm trẻ thành công');
        navigate('/');
      }
    },
    onError: (error) => {
      const errorData = error.response?.data?.error;
      if (errorData?.details) {
        setErrors({ general: errorData.details.join(', ') });
      } else {
        setErrors({ general: errorData?.message || 'An error occurred' });
      }
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data) => childrenApi.update(childId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['dashboard']);
      queryClient.invalidateQueries(['children']);
      queryClient.invalidateQueries(['child', childId]);
      sessionStorage.setItem('flash_notice', 'Đã cập nhật thông tin con thành công!');
      navigate('/');
    },
    onError: (error) => {
      const errorData = error.response?.data?.error;
      if (errorData?.details) {
        setErrors({ general: errorData.details.join(', ') });
      } else {
        setErrors({ general: errorData?.message || 'An error occurred' });
      }
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    // Validate (Vietnamese error messages to match existing tests)
    const newErrors = {};
    if (!yearOfBirth) {
      newErrors.yearOfBirth = 'Năm sinh không thể để trắng';
    }
    if (!username.trim()) {
      newErrors.username = 'Tên đăng nhập không thể để trắng';
    }
    if (!isEdit && !password) {
      newErrors.password = 'Mật khẩu không thể để trắng';
    }
    if (password && password.length < 6) {
      newErrors.password = 'Mật khẩu phải có ít nhất 6 ký tự';
    }
    if (!avatar) {
      newErrors.avatar = 'Bạn cần chọn con giáp đại diện';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const data = {
      year_of_birth: parseInt(yearOfBirth, 10),
      username: username.trim(),
      avatar,
    };

    // Only include password if provided
    if (password) {
      data.password = password;
    }

    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-12 col-md-8 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-header bg-primary text-white">
              <h4 className="mb-0">
                {isEdit
                  ? t('onboarding.child_form.edit_title')
                  : t('onboarding.child_form.create_title')}
              </h4>
            </div>
            <div className="card-body">
              {errors.general && (
                <div className="alert alert-danger" role="alert">
                  {errors.general}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="username" className="form-label">
                    {t('onboarding.child_form.username_label')}
                  </label>
                  <input
                    type="text"
                    id="username"
                    className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                    placeholder={t('onboarding.child_form.username_placeholder')}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={isSubmitting}
                  />
                  {errors.username && (
                    <div className="invalid-feedback">{errors.username}</div>
                  )}
                  <div className="form-text">
                    {t('onboarding.child_form.username_help')}
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="yearOfBirth" className="form-label">
                    {t('onboarding.child_form.year_of_birth_label')}
                  </label>
                  <input
                    type="text"
                    id="yearOfBirth"
                    className={`form-control ${errors.yearOfBirth ? 'is-invalid' : ''}`}
                    placeholder={t('onboarding.child_form.year_of_birth_placeholder')}
                    value={yearOfBirth}
                    onChange={(e) => setYearOfBirth(e.target.value)}
                    disabled={isSubmitting}
                  />
                  {errors.yearOfBirth && (
                    <div className="invalid-feedback">{errors.yearOfBirth}</div>
                  )}
                </div>

                <div className="mb-3">
                  <label htmlFor="password" className="form-label">
                    {t('onboarding.child_form.password_label')}
                    {isEdit && <span className="text-muted ms-1">(optional)</span>}
                  </label>
                  <input
                    type="password"
                    id="password"
                    className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                    placeholder={t('onboarding.child_form.password_placeholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isSubmitting}
                  />
                  {errors.password && (
                    <div className="invalid-feedback">{errors.password}</div>
                  )}
                  <div className="form-text">
                    {t('onboarding.child_form.password_help')}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label">
                    {t('onboarding.child_form.avatar_label')}
                  </label>
                  <div className="row g-2">
                    {ZODIAC_ANIMALS.map((animal) => (
                      <div key={animal} className="col-4 col-md-3">
                        <div
                          className={`card cursor-pointer ${
                            avatar === animal ? 'border-primary bg-primary-subtle' : ''
                          }`}
                          onClick={() => setAvatar(animal)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="card-body text-center py-2">
                            <span
                              className={`zodiac-icon zodiac-icon-${animal} zodiac-icon-sm d-inline-block`}
                              role="img"
                              aria-label={animal}
                            ></span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {errors.avatar && (
                    <div className="text-danger mt-2 small">{errors.avatar}</div>
                  )}
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/')}
                    disabled={isSubmitting}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary flex-grow-1"
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? (isEdit
                          ? t('onboarding.child_form.updating')
                          : t('onboarding.child_form.creating'))
                      : (isEdit
                          ? t('onboarding.child_form.submit_update')
                          : t('onboarding.child_form.submit_create'))}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChildForm;
